/**
 * Track App push prompt integration.
 * Session key is per patient (facility + user_uid), not per order, so we only prompt once per patient per session.
 *
 * ВАЖНО: web-push менеджер грузим лениво через dynamic import,
 * чтобы избежать побочных эффектов при импорте (как в патче 105).
 */

/** Key for "already prompted in this session" — per patient so we don't prompt on every new order link. */
export function shouldPromptKeyForPatient(facility, userUid) {
  return `push_prompted_facility_${facility}_user_${userUid}`
}

async function getWebPushManager() {
  console.log('[push] getWebPushManager: dynamic import start')
  const { webPushManager } = await import('@/push/web-push')
  console.log('[push] getWebPushManager: dynamic import done')
  return webPushManager
}

export async function initPushPrompt({ orderUid, orderHash, notificationType = 'order_status' }) {
  console.log('[push] initPushPrompt called', { orderUid, orderHash, notificationType })

  if (!orderUid || !orderHash) {
    console.log('[push] initPushPrompt exit: missing orderUid or orderHash', { orderUid, orderHash })
    return
  }

  try {
    const webPushManager = await getWebPushManager()

    console.log('[push] initPushPrompt: calling init()')
    const initOk = await webPushManager.init()
    console.log('[push] init() result:', initOk)

    const browserSubscribed = await webPushManager.isSubscribed()
    console.log('[push] isSubscribed:', browserSubscribed)
    if (browserSubscribed) {
      return
    }

    console.log('[push] fetching eligibility', { orderUid, orderHash, notificationType })
    const eligibility = await webPushManager.fetchEligibility(orderUid, orderHash, notificationType)
    console.log('[push] eligibility response full:', JSON.stringify(eligibility, null, 2))
    // Prefer client state: if this browser has no subscription, allow prompt when push is enabled,
    // even when server has has_server_subscription (e.g. stale or other-device subscription).
    const allowedByServer = eligibility?.should_prompt === true
    const pushEnabledNoSubInBrowser = eligibility?.success === true
      && eligibility?.facility_enabled === true
      && eligibility?.global_enabled === true
    if (!allowedByServer && !pushEnabledNoSubInBrowser) {
      console.log('[push] should_prompt is false and push not enabled for facility/global, exiting')
      return
    }

    // Session-scoped per patient: one prompt per (facility, user) per session, not per order
    const key = shouldPromptKeyForPatient(eligibility.facility, eligibility.user_uid)
    console.log('[push] session key:', key)
    if (sessionStorage.getItem(key) === 'true') {
      console.log('[push] already prompted in this session, exiting')
      return
    }

    console.log('[push] scheduling prompt in 2s')
    setTimeout(async () => {
      try {
        const webPushManagerInner = await getWebPushManager()
        await webPushManagerInner.promptSubscription(orderUid, orderHash, {
          title: 'Enable Push Notifications',
          message: 'Get instant updates about your delivery status via browser notifications.',
        })
      } catch (e) {
        console.error('[push] promptSubscription failed', e)
      } finally {
        sessionStorage.setItem(key, 'true')
      }
    }, 2000)
  } catch (e) {
    console.error('[push] initPushPrompt catch', e)
    // On error, still set key for this order so we don't retry in a loop (fallback: per-order key)
    try {
      const fallbackKey = `push_prompted_order_${orderUid}`
      sessionStorage.setItem(fallbackKey, 'true')
    } catch (_) {
      // ignore
    }
    // silent
  }
}

/**
 * Подтверждение просмотра пуша при открытии из уведомления.
 * Ленивая загрузка webPushManager внутри.
 */
export async function confirmViewedIfOpenedFromPush(orderUid, orderHash, openedFromPush) {
  console.log('[push] confirmViewedIfOpenedFromPush called', { orderUid, orderHash, openedFromPush })
  if (!openedFromPush) {
    return
  }
  if (!orderUid || !orderHash) {
    console.log('[push] confirmViewedIfOpenedFromPush exit: missing orderUid or orderHash')
    return
  }
  try {
    const webPushManager = await getWebPushManager()
    console.log('[push] confirmViewedIfOpenedFromPush: calling confirmViewed')
    await webPushManager.confirmViewed(orderUid, orderHash, 'push')
  } catch (e) {
    console.error('[push] confirmViewedIfOpenedFromPush catch', e)
  }
}
